import { BsPlusCircle, BsTrash } from 'react-icons/bs';
import { FC, useMemo, useRef, useState } from 'react';
import { generalHelpers } from '@monorepo/helpers';
import { hideLoadingModalReducer, setAlertResponse, setValuesAndOpenAlertModalReducer, showLoadingModalReducer } from 'src/store';
import { typeUser } from '@monorepo/models';
import { useDispatch } from 'react-redux';
import { useGetConfig, useGetIsDarkMode, useGetShowingLoadingModal } from 'src/custom-hooks';
import { userServices } from 'src/services';

type propsType = {
    closeSection: () => void;
    users: typeUser[];
};

export const UsersMasiveEdit: FC<propsType> = ({ closeSection, users }) => {
    const [errorMessage, setErrorMessage] = useState('');
    const [usersToSave, setUsersToSave] = useState(users);
    const config = useGetConfig();
    const dispatch = useDispatch();
    const isDarkMode = useGetIsDarkMode();
    const now = useRef(Date.now()).current;
    const showingLoadingModal = useGetShowingLoadingModal();

    const getNewUSer = (): typeUser => ({
        cartAssignments: [],
        congregation: config.congregation,
        email: '',
        group: null,
        groupIsTheAssistance: false,
        groupIsTheOverseer: false,
        hthAssignments: [],
        id: Date.now(),  // for frontend only
        isActive: true,
        isSuperAdmin: false,
        meetingsWithAssignments: [],
        otpOptions: [],
        phoneAssignments: [],
        publicMinistryAssignments: [],
        recoveryOptions: [],
        roles: [],
        webAuthnAuthenticators: []
    });

    const somethingChanged: boolean = useMemo(() =>
        JSON.stringify(users) !== JSON.stringify(usersToSave)
    , [users, usersToSave]);

    const isValid: boolean = useMemo(() => {
        if (!somethingChanged) {
            setErrorMessage('No se hizo ningún cambio');
            return false;
        }
        const emailCount = new Map<string, number>();
        for (const user of usersToSave) {
            if (!user.name) {
                setErrorMessage('A un usuario le falta el nombre');
                return false;
            }
            if (!user.lastName) {
                setErrorMessage(`Al usuario ${user.name} le falta el apellido`);
                return false;
            }
            if (user.id > now && (!user.passwordNew || user.passwordNew.length < 8)) {
                setErrorMessage(`Al usuario ${user.name} ${user.lastName} le falta una contraseña de al menos 8 caracteres`);
                return false;
            }
            if (!user.email) {
                setErrorMessage(`Al usuario ${user.name} ${user.lastName} le falta el email`);
                return false;
            }
            if (!generalHelpers.IsValidEmail(user.email)) {
                setErrorMessage(`El email de ${user.name} ${user.lastName} tiene un formato inválido, debe ser signo + y unos 12-14 dígitos contando número de país y de área (normalmente +54911 y los 8 siguientes)`);
                return false;
            }
            emailCount.set(user.email, (emailCount.get(user.email) || 0) + 1);
            if (user.phoneNumber && !user.phoneNumber.includes('***') && !generalHelpers.IsValidPhoneNumber(user.phoneNumber)) {
                setErrorMessage(`El teléfono de ${user.name} ${user.lastName} tiene un formato inválido`);
                return false;
            }
            if (!user.group && user.groupIsTheAssistance) {
                setErrorMessage(`El usuario ${user.name} ${user.lastName} no está en ningún grupo pero está marcado como Auxiliar de grupo`);
                return false;
            }
            if (!user.group && user.groupIsTheOverseer) {
                setErrorMessage(`El usuario ${user.name} ${user.lastName} no está en ningún grupo pero está marcado como Superintendente de grupo`);
                return false;
            }
            if (!!config.numberOfGroups && usersToSave.some(u => u.group)) {
                for (const group of [... new Set(usersToSave.filter(u => u.group).map(u => u.group))].sort((g1, g2) => g1! - g2!)) {
                    if (!usersToSave.filter(u => u.group === group && u.groupIsTheOverseer).length) {
                        setErrorMessage(`El grupo ${group} no tiene Superintendente`);
                        return false;
                    }
                    if (usersToSave.filter(u => u.group === group && u.groupIsTheOverseer).length !== 1) {
                        setErrorMessage(`El grupo ${group} tiene más de un Superintendente`);
                        return false;
                    }
                    if (!usersToSave.filter(u => u.group === group && u.groupIsTheAssistance).length) {
                        setErrorMessage(`El grupo ${group} no tiene Auxiliar`);
                        return false;
                    }
                    if (usersToSave.filter(u => u.group === group && u.groupIsTheAssistance).length !== 1) {
                        setErrorMessage(`El grupo ${group} tiene más de un Auxiliar`);
                        return false;
                    }
                }
            }
        }
        const duplicatedEmails = Array.from(emailCount.entries())
            .filter(([_, count]) => count > 1)
            .map(([email]) => email)
            .join(', ')
        ;
        if (duplicatedEmails.length > 0) {
            setErrorMessage(`Hay emails repetidos: ${duplicatedEmails}`);
            return false;
        }
        setErrorMessage('');
        return true;
    }, [somethingChanged, usersToSave]);

    const saveHandler = () => {
        if (!isValid) {
            return;
        }
        const usersToDelete = users.filter(u => !usersToSave.map(_u => _u.id).includes(u.id));
        const usersToAdd = usersToSave.filter(u => !users.map(_u => _u.id).includes(u.id));
        const usersToEdit = usersToSave.filter(u => {
            const wasNotAdded = !usersToAdd.find(_u => _u.id === u.id);
            const wasNotRemoved = !usersToDelete.find(_u => _u.id === u.id);
            const wasEdited = wasNotAdded && wasNotRemoved && JSON.stringify(u) !== JSON.stringify(users.find(_u => _u.id === u.id));
            return wasEdited;
        });
        dispatch(setValuesAndOpenAlertModalReducer({
            title: 'Confirmar',
            message: 'Se van a guardar todos los cambios',
            mode: 'confirm',
            execution: async () => {
                dispatch(showLoadingModalReducer());
                const response = await userServices.EditMany(usersToEdit, usersToAdd, usersToDelete);
                dispatch(hideLoadingModalReducer());
                if (!response.success) {
                    dispatch(setAlertResponse({ response }));
                    return;
                }
                dispatch(setValuesAndOpenAlertModalReducer({
                    title: 'Logrado',
                    message: 'Se hicieron todos los cambios',
                    animation: 1,
                    mode: 'alert'
                }));
                closeSection();
            }
        }));
    }

    const deleteUserHandler = (user: typeUser): void => {
        if (user.roles.length) {
            dispatch(setValuesAndOpenAlertModalReducer({
                title: 'No se puede eliminar a un usuario con roles; remover primero los roles del usuario en la otra pantalla',
                message: '',
                animation: 2,
                mode: 'alert'
            }));
            return;
        }
        if (user.isActive) {
            dispatch(setValuesAndOpenAlertModalReducer({
                title: 'No se puede eliminar a un usuario activo; desmarcar primero el campo "Activado"',
                message: '',
                animation: 2,
                mode: 'alert'
            }));
            return;
        }
        dispatch(setValuesAndOpenAlertModalReducer({
            title: '¿Eliminar?',
            message: 'En caso de querer revertir esta eliminación antes de guardar los cambios, habrá que cancelar todo lo hecho hasta acá. Si se guardan los cambios, el usuario será eliminado sin vuelta atrás.',
            mode: 'confirm',
            animation: 3,
            execution: () => {
                setUsersToSave(x => x.filter(_u => _u.id !== user.id));
            }
        }));
    }

    return (
        <div>
            <div className={'d-flex justify-content-center my-4'}>
                <button className={'btn btn-general-red'}
                    onClick={() => setUsersToSave(x => [getNewUSer(), ...x])}
                >
                    <BsPlusCircle className={'mb-1 me-2'} />Crear Usuario
                </button>
            </div>
            <div className={'table-responsive'}>
                <table className={`table table-bordered table-hover ${isDarkMode ? 'table-dark border' : ''} small w-100 mx-auto`}>
                    <thead>
                        <tr>
                            <th>Eliminar</th>
                            <th>Nombre</th>
                            <th>Apellido</th>
                            <th>Contraseña</th>
                            <th>Email</th>
                            <th>Teléfono</th>
                            <th style={{ width: '80px' }}>Grupo</th>
                            <th>Sup. Grupo</th>
                            <th>Aux. Grupo</th>
                            <th>Roles</th>
                            <th>Activo</th>
                        </tr>
                    </thead>
                    <tbody>
                        {usersToSave.map(u =>
                            <tr key={u.id}>
                                <td>
                                    <button className={'btn btn-sm btn-general-red w-100'}
                                        onClick={() => deleteUserHandler(u)}
                                    >
                                        <BsTrash className={'mb-1'} />
                                    </button>
                                </td>
                                <td>
                                    <input type={'text'}
                                        className={'form-control form-control-sm'}
                                        value={u.name}
                                        onChange={e => {
                                            const v = e.target.value;
                                            if (v.length > 100) {
                                                return;
                                            }
                                            setUsersToSave(x => x.map(_u => _u.id === u.id ? ({
                                                ...u,
                                                name: v
                                            }) : _u));
                                        }}
                                    />
                                </td>
                                <td>
                                    <input type={'text'}
                                        className={'form-control form-control-sm'}
                                        value={u.lastName}
                                        onChange={e => {
                                            const v = e.target.value;
                                            if (v.length > 100) {
                                                return;
                                            }
                                            setUsersToSave(x => x.map(_u => _u.id === u.id ? ({
                                                ...u,
                                                lastName: v
                                            }) : _u));
                                        }}
                                    />
                                </td>
                                <td>
                                    {u.id > now &&
                                        <input type={'text'}
                                            className={'form-control form-control-sm'}
                                            value={u.passwordNew ?? ''}
                                            onChange={e => {
                                                const v = e.target.value;
                                                if (v.length > 100) {
                                                    return;
                                                }
                                                setUsersToSave(x => x.map(_u => _u.id === u.id ? ({
                                                    ...u,
                                                    passwordNew: v
                                                }) : _u));
                                            }}
                                        />
                                    }
                                </td>
                                <td>
                                    <input type={'email'}
                                        className={'form-control form-control-sm'}
                                        value={u.email}
                                        onChange={e => {
                                            const v = e.target.value;
                                            if (v.length > 100) {
                                                return;
                                            }
                                            setUsersToSave(x => x.map(_u => _u.id === u.id ? ({
                                                ...u,
                                                email: v
                                            }) : _u));
                                        }}
                                    />
                                </td>
                                <td>
                                    <input type={'tel'}
                                        className={'form-control form-control-sm'}
                                        value={u.phoneNumber ?? ''}
                                        onChange={e => {
                                            const v = e.target.value.split(' ').join('').split('-').join('');
                                            if (v.length > 100) {
                                                return;
                                            }
                                            setUsersToSave(x => x.map(_u => _u.id === u.id ? ({
                                                ...u,
                                                phoneNumber: v
                                            }) : _u));
                                        }}
                                    />
                                </td>
                                <td className={'text-center'}>
                                    <input type={'number'}
                                        className={'form-control form-control-sm'}
                                        value={u.group || ''}
                                        onChange={e => {
                                            const v = parseInt(e.target.value) || 0;
                                            if (v > config.numberOfGroups) {
                                                return;
                                            }
                                            setUsersToSave(x => x.map(_u => _u.id === u.id ? ({
                                                ...u,
                                                group: v
                                            }) : _u));
                                        }}
                                    />
                                </td>
                                <td className={'text-center'}>
                                    <input type={'checkbox'}
                                        className={'form-check-input checkbox-md checkbox-blue me-2'}
                                        checked={!!u.groupIsTheOverseer}
                                        onChange={() => {
                                            setUsersToSave(x => x.map(_u => _u.id === u.id ? ({
                                                ...u,
                                                groupIsTheAssistance: false,
                                                groupIsTheOverseer: !u.groupIsTheOverseer
                                            }) : _u));
                                        }}
                                    />
                                </td>
                                <td className={'text-center'}>
                                    <input type={'checkbox'}
                                        className={'form-check-input checkbox-md checkbox-blue me-2'}
                                        checked={!!u.groupIsTheAssistance}
                                        onChange={() => {
                                            setUsersToSave(x => x.map(_u => _u.id === u.id ? ({
                                                ...u,
                                                groupIsTheAssistance: !u.groupIsTheAssistance,
                                                groupIsTheOverseer: false
                                            }) : _u));
                                        }}
                                    />
                                </td>
                                <td>
                                    {!!u.roles.length ? u.rolesAsText : '-'}
                                </td>
                                <td className={'text-center'}>
                                    <input type={'checkbox'}
                                        className={'form-check-input checkbox-md checkbox-red me-2'}
                                        checked={!!u.isActive}
                                        onChange={() => {
                                            setUsersToSave(x => x.map(_u => _u.id === u.id ? ({
                                                ...u,
                                                isActive: !u.isActive
                                            }) : _u));
                                        }}
                                    />
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            {errorMessage &&
                <div className={'alert alert-danger mt-3'} role={'alert'}>
                    {errorMessage}
                </div>
            }
            <div className={'d-flex justify-content-center gap-2 pb-5 mt-5'}>
                {somethingChanged &&
                    <button className={'btn btn-general-blue'}
                        style={{ width: '150px' }}
                        onClick={saveHandler}
                        disabled={!isValid || showingLoadingModal}
                    >
                        Guardar
                    </button>
                }
                <button className={'btn btn-dark'}
                    style={{ width: '150px' }}
                    onClick={() => {
                        somethingChanged ?
                            dispatch(setValuesAndOpenAlertModalReducer({
                                title: '¿Cancelar?',
                                message: 'Se van a perder todos los cambios hechos hasta acá',
                                mode: 'confirm',
                                animation: 3,
                                execution: closeSection
                            }))
                            :
                            closeSection()
                    }}
                    disabled={showingLoadingModal}
                >
                    Cancelar
                </button>
            </div>
        </div>
    );
}
